import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import { uploadFailure } from '../api/hooks';
import type { Country } from '../api/types';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Note } from '../ui';
import { AuthPage, Label } from './auth-parts';
import {
  emptyApplyForm, emptyApplyRef, withCountry, withDefaultPortal, withField, withPortal,
  withRefAdded, withRefField, withRefRemoved, type ApplyForm, type ApplyRef,
} from './apply-form';

interface OpenPortal { code: string; name: string; asksBuyingGroup: boolean }

/**
 * ON-02a — what the submission answers with, including the one-sitting UPLOAD TICKET.
 *
 * `upload.token` is a write credential and it exists ONLY in this component's state, for as long
 * as P-03 is on screen. It is never put in the URL, never written to storage and never logged: it
 * is the whole of the access control on a public write, and the server will not issue another.
 * An applicant who leaves this page emails anything further to Miraki, who file it themselves —
 * see `kyc-documents.ts` for why there is no resume link.
 */
interface Submitted {
  reference: string;
  status: string;
  upload: { token: string; expiresAt: string; documentsAccepted: number };
}

/**
 * P-02 and P-03 — archetype 1, centred single column.
 *
 * The interesting part is ON-03: the registration field renames and revalidates
 * itself from the country pack. It says ABN for Australia and GSTIN for India
 * because those rows say so, not because this file knows anything about either
 * country. Adding a jurisdiction changes this screen without touching it.
 */
export function Apply({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const [portals, setPortals] = useState<OpenPortal[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  /*
   * Held as one object and updated ONLY through `apply-form.ts`, which takes the
   * previous state rather than a captured copy of it. The note at the top of
   * that file is the whole reason it exists: an applicant lost seven filled-in
   * fields to a stale snapshot.
   */
  const [form, setForm] = useState<ApplyForm>(emptyApplyForm);
  /*
   * ON-02 — trade references, which the applicant adds as they go.
   *
   * Starts as ONE empty row rather than none. A bare "Add a reference" button on a long
   * public form is read as optional and skipped; a row already present is read as something
   * to fill in. Nothing is sent for a row left blank, so the generous default costs nothing.
   */
  const [refs, setRefs] = useState<ApplyRef[]>([{ ...emptyApplyRef }]);
  const [regCheck, setRegCheck] = useState<{ ok: boolean; message?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Array<{ path: string; message: string }>>([]);
  const [done, setDone] = useState<Submitted | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<OpenPortal[]>('/portals/open', { noPortal: true }),
      api.get<Country[]>('/countries', { noPortal: true }),
    ])
      .then(([p, c]) => {
        setPortals(p);
        setCountries(c);
        setForm((f) => withDefaultPortal(f, p[0]?.code));
      })
      .catch((e) => setLoadError(e instanceof ApiError ? e.message : 'Could not load the form'));
  }, []);

  // Whether to ask is the portal's to say, never this file's: no portal code is compared here.
  const asksBuyingGroup = portals.find((p) => p.code === form.portalCode)?.asksBuyingGroup === true;

  const pack = useMemo(
    () => countries.find((c) => c.code === form.countryCode),
    [countries, form.countryCode],
  );

  /*
   * The country's own word for its postal code, once one is chosen. Before that it is the
   * neutral term — a form that opens asking for a "ZIP code" has already assumed where the
   * applicant is.
   */
  const postalLabel = pack?.postalCodeLabel ?? t('apply.postalCode');

  /*
   * ON-03 — what the pack asks for, in the pack's own words.
   *
   * "a 15-digit number", never `^\d{15}$`. The words come from the country list beside the
   * pattern rather than being worked out here, so this hint and the refusal underneath it
   * are the same sentence by construction. A pack whose rule cannot be put plainly says
   * nothing about the shape instead of printing the expression.
   *
   * "Spaces and hyphens are fine" is a promise the server keeps: a registration ID is
   * stripped of both before it is tested and before it is stored.
   */
  const regHint = pack
    ? pack.registrationIdFormat
      ? t('apply.regHintFormat', {
          country: pack.name, label: pack.registrationIdLabel, format: pack.registrationIdFormat,
        })
      : t('apply.regHint', { country: pack.name, label: pack.registrationIdLabel })
    : t('apply.regChooseCountry');

  // Check the registration against the country's own rule as the applicant
  // leaves the field, so they find out here rather than after submitting.
  async function checkRegistration() {
    if (!form.countryCode || !form.registrationId) return setRegCheck(null);
    try {
      const r = await api.post<{ ok: boolean; message?: string }>(
        '/countries/check-registration',
        { countryCode: form.countryCode, registrationId: form.registrationId },
        { noPortal: true },
      );
      setRegCheck(r);
    } catch {
      setRegCheck(null);
    }
  }

  const set = (k: keyof ApplyForm) => (e: { target: { value: string } }) =>
    setForm((f) => withField(f, k, e.target.value));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors([]);
    try {
      const body: Record<string, string> = {
        portalCode: form.portalCode,
        businessName: form.businessName,
        firstName: form.firstName,
        lastName: form.lastName,
        contactEmail: form.contactEmail,
        countryCode: form.countryCode,
        registrationId: form.registrationId,
      };
      // Optional fields are omitted rather than sent empty: the contract types
      // them as absent-or-present, and "" is neither.
      if (form.phone) body.phone = form.phone;
      if (form.city) body.city = form.city;
      if (form.postalCode) body.postalCode = form.postalCode;
      if (form.businessType) body.businessType = form.businessType;
      // Sent only where the portal asks: one that does not refuses it, so a value typed before
      // switching portal must not travel. A blank one is left out, so the service's "needs the
      // buying group" answers rather than a bare length error.
      if (asksBuyingGroup && form.buyingGroup.trim()) body.buyingGroup = form.buyingGroup.trim();
      if (form.note) body.note = form.note;

      // A reference with no company is a row the applicant started and abandoned, not a
      // reference. Dropped here so an untouched form sends none at all.
      const named = refs.filter((r) => r.company.trim().length >= 2);
      if (named.length) (body as Record<string, unknown>).tradeReferences = named;

      setDone(await api.post<Submitted>('/applications', body, { noPortal: true }));
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fieldErrors);
      } else {
        setError('Could not submit the application');
      }
    } finally {
      setBusy(false);
    }
  }

  /* ---------------------------------------------------------- P-03 */
  if (done) {
    return (
      <AuthPage
        eyebrow={t('auth.eyebrow')}
        title={t('apply.received')}
        lede={<Trans i18nKey="apply.referenceLede" values={{ reference: done.reference }} components={{ b: <b /> }} />}
      >
        {/*
          * "What happens next" is a SUBHEAD, not the page's eyebrow.
          *
          * It borrowed `.mk-auth-eyebrow` — a centred, tiny, letterspaced style written for
          * the one line above an h1 — and then overrode it inline to sit left, which left it
          * flush against the reference line above (the eyebrow declares no top margin, and
          * Tailwind's preflight zeroes the lede's bottom one) and ranged left against a page
          * that is otherwise centred. Its own class, with its spacing from the token scale:
          * UI-04 means no size or spacing literal belongs in a component.
          */}
        <h3 className="mk-auth-subhead">{t('apply.whatNext')}</h3>
        <div className="mk-auth-next">
          <ol className="mk-auth-steps">
            <li>{t('apply.next1')}</li>
            <li>{t('apply.next2')}</li>
            <li>{t('apply.next3')}</li>
          </ol>
          <Note>{t('apply.decidedByPerson')}</Note>
        </div>
        <hr className="mk-auth-rule" />
        {/*
          * ON-02a — the documents, after "what happens next" and before the way out.
          *
          * In that order on purpose: the application is already in, so the reassurance comes
          * first and the ask second. An applicant who stops reading here has still applied.
          */}
        <Documents done={done} />
        <hr className="mk-auth-rule" />
        <div className="mk-auth-foot">
          <button type="button" className="mk-auth-alt" onClick={onBack}>{t('auth.backToSignIn')}</button>
        </div>
      </AuthPage>
    );
  }

  /* ---------------------------------------------------------- P-02 */
  return (
    <AuthPage
      eyebrow={t('auth.eyebrow')}
      title={t('apply.title')}
      lede={t('apply.lede')}
      wide
    >
      <form onSubmit={(e) => void submit(e)} noValidate>
        {loadError ? <Alert tone="stop">{loadError}</Alert> : null}
        {error ? (
          <Alert tone="stop">
            {error}
            {fieldErrors.length ? (
              <ul className="mk-auth-fielderrs">
                {fieldErrors.map((f) => <li key={f.path}>{f.path}: {f.message}</li>)}
              </ul>
            ) : null}
          </Alert>
        ) : null}

        <div className="mk-auth-field">
          <div className="mk-auth-labelrow"><Label htmlFor="portal" required>{t('apply.portal')}</Label></div>
          <select
            id="portal" value={form.portalCode} required
            onChange={(e) => setForm((f) => withPortal(f, e.target.value))}
          >
            {portals.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
          <span className="mk-auth-hint">{t('apply.portalHint')}</span>
        </div>

        <div className="mk-auth-two">
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="biz" required>{t('apply.businessName')}</Label></div>
            <input id="biz" value={form.businessName} onChange={set('businessName')} required minLength={2} />
          </div>
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="city">{t('apply.city')}</Label></div>
            <input id="city" value={form.city} onChange={set('city')} />
          </div>
          {/*
            * ON-03 validates this against the chosen country's pack, so the label is the
            * country's own word — Postcode, ZIP code, PIN code — and it is never REQUIRED:
            * the UAE has no postal code system at all, and a mandatory field nobody can
            * satisfy gets a full stop typed into it.
            */}
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow">
              <Label htmlFor="postal">{postalLabel}</Label>
            </div>
            <input id="postal" value={form.postalCode} onChange={set('postalCode')} />
          </div>
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="bizType">{t('apply.businessType')}</Label></div>
            <input
              id="bizType" value={form.businessType} onChange={set('businessType')}
              placeholder={t('apply.businessTypeHint')}
            />
          </div>
          {/*
            * ON-02 asks for the name in two parts, and the split is not cosmetic: one box
            * cannot be addressed correctly in a letter or a greeting, and guessing where to
            * cut it is wrong in exactly the markets this portal serves.
            */}
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="firstName" required>{t('apply.firstName')}</Label></div>
            <input id="firstName" value={form.firstName} onChange={set('firstName')} required minLength={1} />
          </div>
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="lastName" required>{t('apply.lastName')}</Label></div>
            <input id="lastName" value={form.lastName} onChange={set('lastName')} required minLength={1} />
          </div>
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="email" required>{t('apply.email')}</Label></div>
            <input id="email" type="email" placeholder={t('auth.emailPlaceholder')}
              value={form.contactEmail} onChange={set('contactEmail')} required />
          </div>
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="phone">{t('apply.phone')}</Label></div>
            <input id="phone" value={form.phone} onChange={set('phone')} />
          </div>
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow">
              <Label htmlFor="country" required>{t('apply.country')}</Label>
            </div>
            {/*
              * ON-03 — the country relabels two fields from its pack and empties none of
              * them. Only the last check is dropped: it was made against the pack the
              * applicant has just moved away from, and it is remade on the next blur.
              */}
            <select
              id="country" value={form.countryCode} required
              onChange={(e) => { setForm((f) => withCountry(f, e.target.value)); setRegCheck(null); }}
            >
              <option value="" disabled>{t('apply.selectCountry')}</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="mk-auth-field">
          <div className="mk-auth-labelrow">
            {/* ON-03 made visible: the label IS the country pack's. */}
            <Label htmlFor="reg" required>{pack ? pack.registrationIdLabel : t('apply.registrationFallback')}</Label>
          </div>
          <input
            id="reg" value={form.registrationId} onChange={set('registrationId')}
            onBlur={() => void checkRegistration()} disabled={!pack} required
          />
          <span
            className="mk-auth-hint"
            style={regCheck ? { color: regCheck.ok ? 'var(--mk-st-done-ink)' : 'var(--mk-st-stop-ink)' } : undefined}
          >
            {regCheck && !regCheck.ok
              ? regCheck.message
              : regCheck?.ok
                ? t('apply.regLooksRight')
                : pack
                  ? regHint
                  : t('apply.regChooseCountry')}
          </span>
        </div>

        {/*
          * ON-02 — trade references.
          *
          * The whole point of a reference is that somebody rings it before approving, so this
          * collects who they are rather than how many there will be. Only the company is
          * required: which contact detail an applicant has to hand varies, and demanding a
          * specific one just gets a plausible-looking fake typed in.
          *
          * A row left untouched is dropped on submit, so the one that appears by default
          * costs nothing — it is there because a bare "Add" button on a long public form
          * reads as optional and gets skipped.
          */}
        <div className="mk-auth-field">
          <div className="mk-auth-labelrow"><Label htmlFor="ref0">{t('apply.tradeReferences')}</Label></div>
          <p className="mk-hint">{t('apply.tradeReferencesHint')}</p>
          {refs.map((r, i) => (
            <div key={i} className="mk-ref-row">
              <input
                id={`ref${i}`} value={r.company} placeholder={t('apply.refCompany')}
                onChange={(e) => setRefs((rs) => withRefField(rs, i, 'company', e.target.value))}
              />
              <input
                value={r.contactName} placeholder={t('apply.refContact')}
                onChange={(e) => setRefs((rs) => withRefField(rs, i, 'contactName', e.target.value))}
              />
              <input
                value={r.email} type="email" placeholder={t('apply.refEmail')}
                onChange={(e) => setRefs((rs) => withRefField(rs, i, 'email', e.target.value))}
              />
              <input
                value={r.phone} placeholder={t('apply.refPhone')}
                onChange={(e) => setRefs((rs) => withRefField(rs, i, 'phone', e.target.value))}
              />
              {refs.length > 1 ? (
                <button type="button" className="mk-btn-link" onClick={() => setRefs((rs) => withRefRemoved(rs, i))}>
                  {t('common.remove')}
                </button>
              ) : null}
            </div>
          ))}
          {refs.length < 10 ? (
            <button
              type="button" className="mk-btn-link"
              onClick={() => setRefs(withRefAdded)}
            >
              {t('apply.addReference')}
            </button>
          ) : null}
        </div>

        {asksBuyingGroup ? (
          <div className="mk-auth-field">
            <div className="mk-auth-labelrow"><Label htmlFor="buying-group" required>{t('apply.buyingGroup')}</Label></div>
            <input id="buying-group" value={form.buyingGroup} onChange={set('buyingGroup')} required maxLength={120} />
            <span className="mk-auth-hint">{t('apply.buyingGroupHint')}</span>
          </div>
        ) : null}

        <div className="mk-auth-field">
          <div className="mk-auth-labelrow"><Label htmlFor="note">{t('apply.note')}</Label></div>
          <textarea id="note" rows={3} value={form.note} onChange={set('note')} />
        </div>

        <button className="mk-auth-submit" type="submit" disabled={busy || regCheck?.ok === false}>
          {busy ? t('apply.submitting') : t('apply.submit')}
        </button>
      </form>

      <hr className="mk-auth-rule" />
      <div className="mk-auth-foot">
        <p>{t('apply.alreadyHaveAccount')}</p>
        <button type="button" className="mk-auth-alt" onClick={onBack}>{t('auth.backToSignIn')}</button>
      </div>
    </AuthPage>
  );
}

/**
 * ON-02a, D94 — the applicant's own upload step, on P-03.
 *
 * **Here and not on P-02, and that is the design rather than a convenience.** The application
 * does not exist until the form is submitted, so there is nothing for a document to belong to
 * before this point; and the client's answer to the gating question was that documents are
 * required to APPROVE and not to apply, so holding the submission hostage to a file the applicant
 * may not have to hand would be the wrong rule as well as the harder one to build.
 *
 * The credential is the ticket in `done.upload.token`, which lives in this component's state and
 * nowhere else. It goes in a header — never the query string, which lands in logs, history and
 * `Referer` — and the request carries no application id at all, because the route accepts none:
 * the ticket IS the address. So an applicant cannot attach to somebody else's application, and
 * nobody can enumerate what another applicant sent, whatever uuids they hold.
 */
function Documents({ done }: { done: Submitted }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState<Array<{ id: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  /*
   * Whether the hour has run out, held as state rather than computed on every render: the window
   * closing is an event the applicant should SEE — mid-upload, with a sentence telling them what
   * to do instead — not a control that silently stops working. Set when the server says so, and
   * once when the expiry passes while the page is open.
   */
  const [closed, setClosed] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const ms = Date.parse(done.upload.expiresAt) - Date.now();
    if (ms <= 0) { setClosed(true); return; }
    const timer = setTimeout(() => setClosed(true), ms);
    return () => clearTimeout(timer);
  }, [done.upload.expiresAt]);

  const limit = done.upload.documentsAccepted;
  const full = sent.length >= limit;

  async function upload(file: File | undefined) {
    if (!file) return;
    setFailed(null);
    setBusy(true);
    try {
      const res = await fetch(
        `/api/applications/documents?${new URLSearchParams({ name: file.name })}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            /*
             * The browser's guess, sent because express decides from it whether to hand the
             * handler a Buffer at all. The server does not trust it and does not store it: it
             * reads the first bytes, and a file that is not one of the six formats is refused
             * however it is labelled.
             */
            'content-type': file.type || 'application/octet-stream',
            'x-miraki-upload-ticket': done.upload.token,
          },
          body: file,
        },
      );
      if (!res.ok) {
        // The server's own sentence. It names the format it read, the number it refused at, or
        // the fact that the window has closed — all of which the applicant has to act on.
        if (res.status === 404) setClosed(true);
        throw await uploadFailure(res);
      }
      const out = await res.json() as { document: { id: string; fileName: string } };
      setSent((s) => [...s, { id: out.document.id, name: out.document.fileName }]);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : t('apply.documentsFailed'));
    } finally {
      setBusy(false);
      // So the same file can be chosen again once a refusal has been dealt with.
      if (picker.current) picker.current.value = '';
    }
  }

  async function drop(id: string) {
    setFailed(null);
    setBusy(true);
    try {
      const res = await fetch(`/api/applications/documents/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'x-miraki-upload-ticket': done.upload.token },
      });
      if (!res.ok) {
        if (res.status === 404) setClosed(true);
        throw await uploadFailure(res);
      }
      setSent((s) => s.filter((d) => d.id !== id));
    } catch (e) {
      setFailed(e instanceof Error ? e.message : t('apply.documentsFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mk-auth-field">
      <h3 className="mk-auth-subhead">{t('apply.documents')}</h3>
      <p className="mk-auth-hint">{t('apply.documentsHint', { limit, mb: 8 })}</p>
      {/*
        * Said plainly, because the rule has two halves and only stating the first one would read
        * as "these are optional": you may submit without them (you already have), AND nothing
        * can be approved until one arrives.
        */}
      <Note>{t('apply.documentsOptional')}</Note>

      {failed ? <Alert tone="stop">{failed}</Alert> : null}

      {sent.length > 0 ? (
        <ul className="mk-auth-steps">
          {sent.map((d) => (
            <li key={d.id}>
              {d.name}
              {!closed ? (
                <>
                  {' · '}
                  {/* The only "replace" there is: remove it and send the right one. */}
                  <button type="button" className="mk-btn-link" disabled={busy} onClick={() => void drop(d.id)}>
                    {t('common.remove')}
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {closed ? (
        <Alert tone="us">{t('apply.documentsClosed', { reference: done.reference })}</Alert>
      ) : (
        <>
          <input
            ref={picker}
            id="apply-docs"
            type="file"
            accept={APPLY_ACCEPT}
            disabled={busy || full}
            onChange={(e) => void upload(e.target.files?.[0])}
          />
          <button
            type="button" className="mk-auth-alt" disabled={busy || full}
            onClick={() => picker.current?.click()}
          >
            {busy ? t('apply.documentsAdding') : t('apply.documentsAdd')}
          </button>
          <span className="mk-auth-hint">
            {full
              ? t('apply.documentsFull', { limit })
              : t('apply.documentsRemaining', { n: sent.length, limit, remaining: limit - sent.length })}
          </span>
          <span className="mk-auth-hint">
            {t('apply.documentsWindow', { reference: done.reference })}
          </span>
        </>
      )}
    </div>
  );
}

/**
 * The picker's filter — a convenience, not a check. A browser's `accept` is a hint a person can
 * override in one click, and the server decides by reading the bytes.
 */
const APPLY_ACCEPT = [
  'application/pdf', 'image/jpeg', 'image/png', 'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');
